import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

export default function useCatalogList() {
  const toast = useToast()
  const route = router.history.current

  const refCatalogsTable = ref(null)

  const tableColumns = [
    {
      key: 'stringID',
      label: 'id',
      sortable: true,
    },
    {
      key: 'image',
      label: 'Image',
      sortable: false,
    },
    {
      key: 'name',
      label: 'Catalog Item Name',
      sortable: true,
    },
    {
      key: 'category',
      label: 'Product Category',
      sortable: false,
    },
    {
      key: 'productCodes',
      label: 'Product Code',
      sortable: false,
    },
    {
      key: 'sclcID',
      label: 'SCLC Package ID',
      sortable: true,
    },
    {
      key: 'code',
      label: 'SCLC Package Code',
      sortable: true,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
    },
    { key: 'actions' },
  ]

  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalCatalogs = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const sortBy = ref(route.query.sortBy ?? 'stringID')
  const isSortDirDesc = ref(route.query.sortDesc ? (route.query.sortDesc !== 'false') : false)
  const createdAtFilter = ref(route.query.createdAt || '')
  const statusFilter = ref(route.query.status || '')
  const categoryFilter = ref([])
  if (Array.isArray(route.query.category)) {
    categoryFilter.value = route.query.category
  } else if (route.query.category) {
    categoryFilter.value = [route.query.category]
  }
  const currentDataLength = ref(0)
  const itemListData = ref([])
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refCatalogsTable.value ? refCatalogsTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalCatalogs.value,
    }
  })

  const refetchData = () => {
    refCatalogsTable.value.refresh()
  }

  const fetchCatalogs = () => {
    store
      .dispatch('app-product-catalog/fetchCatalogs', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        categories: categoryFilter.value,
        status: statusFilter.value,
        createdAt: createdAtFilter.value,
      },
      { root: true })
      .then(response => {
        const { catalogs, total } = response.data
        itemListData.value = catalogs
        currentDataLength.value = catalogs.length
        totalCatalogs.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              category: categoryFilter.value,
              status: statusFilter.value,
              createdAt: createdAtFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  let timer = null
  watch([categoryFilter, statusFilter, createdAtFilter], () => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      currentPage.value = 1
      fetchCatalogs()
    }, 1000)
  }, { deep: true })

  watch([searchQuery], (current, prev) => {
    const currentVal = current[0]
    const prevVal = prev[0]
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      if ((!currentVal && prevVal) || (currentVal && currentVal.length > 2)) {
        currentPage.value = 1
        fetchCatalogs()
      }
    }, 1000)
  }, { deep: true })

  watch([sortBy, perPage, isSortDirDesc, currentPage], () => {
    fetchCatalogs()
  }, { deep: true })

  fetchCatalogs()

  return {
    fetchCatalogs,
    tableColumns,
    perPage,
    currentPage,
    totalCatalogs,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refCatalogsTable,
    refetchData,
    currentDataLength,
    itemListData,
    categoryFilter,
    statusFilter,
    createdAtFilter,
  }
}
